import { Component, HostListener, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { IconComponent } from '../../icon/icon.component';
import { ClickOutsideDirective } from '../../directives/click-outside.directive';
import { NavigationContextService } from '../../../core/services/navigation-context.service';
import { FavoritesService } from '../../../core/services/favorites.service';
import { CurrentUserService } from '../../../core/services/current-user.service';
import { NotificationsService } from '../../../core/services/notifications.service';
import { AccessService } from '../../../core/services/access.service';
import { initialsFor } from '../../utils/initials.util';
import { NotificationItem } from '../../../core/models/notification.model';
import { AuthenticationService } from '@app/core/services';

@Component({
  selector: 'pp-topbar',
  standalone: true,
  imports: [CommonModule, RouterModule, IconComponent, ClickOutsideDirective],
  templateUrl: './topbar.component.html',
})
export class TopbarComponent {
  private readonly authService = inject(AuthenticationService);
  private readonly navContext = inject(NavigationContextService);
  private readonly favoritesService = inject(FavoritesService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly notificationsService = inject(NotificationsService);
  private readonly router = inject(Router);

  /** Modules the user's permissions allow (module switcher). */
  readonly allModules = inject(AccessService).navItems;

  readonly activeModule = this.navContext.activeModule;
  readonly activeChild = this.navContext.activeChild;
  readonly sectionLabel = this.navContext.pageLabel;

  readonly moduleMenuOpen = signal(false);
  readonly userMenuOpen = signal(false);
  readonly notificationsMenuOpen = signal(false);

  private readonly favoritedSections = toSignal(this.favoritesService.getAll(), { initialValue: [] as string[] });

  readonly isFavorited = computed(() => {
    const child = this.activeChild();
    return child ? this.favoritedSections().includes(child.route) : false;
  });

  readonly currentUser = toSignal(this.currentUserService.getUser(), { initialValue: this.currentUserService.current });
  readonly userInitials = computed(() => initialsFor(this.currentUser().name));

  readonly notifications = toSignal(this.notificationsService.getAll(), { initialValue: [] as NotificationItem[] });
  readonly unreadCount = computed(() => this.notifications().filter((n) => !n.read).length);

  toggleModuleMenu(): void {
    this.userMenuOpen.set(false);
    this.notificationsMenuOpen.set(false);
    this.moduleMenuOpen.update((v) => !v);
  }

  toggleUserMenu(): void {
    this.moduleMenuOpen.set(false);
    this.notificationsMenuOpen.set(false);
    this.userMenuOpen.update((v) => !v);
  }

  toggleNotificationsMenu(): void {
    this.moduleMenuOpen.set(false);
    this.userMenuOpen.set(false);
    this.notificationsMenuOpen.update((v) => !v);
  }

  toggleFavorite(): void {
    const child = this.activeChild();
    if (!child) return;
    this.favoritesService.toggle(child.route);
  }

  onNotificationClick(item: NotificationItem): void {
    this.notificationsService.markRead(item.id);
    this.notificationsMenuOpen.set(false);
  }

  markAllNotificationsRead(): void {
    this.notificationsService.markAllRead(this.notifications().map((n) => n.id));
  }

  public async signOut() {
    this.closeMenus();
    await this.authService.logout();
  }

  closeMenus(): void {
    this.moduleMenuOpen.set(false);
    this.userMenuOpen.set(false);
    this.notificationsMenuOpen.set(false);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeMenus();
  }
}
